import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { InterviewService } from './core/interview/interview.service';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { fromEvent, map, merge, Observable, Observer } from 'rxjs';
import { DarkModeColorDirective } from './basic/dark-mode/dark-mode-color.directive';
import { GlobalLoadingBarComponent } from './basic/loading/global-loading-bar/global-loading-bar.component';
import { ContentModalComponent } from './basic/modal/content-modal/content-modal.component';
import { VersionUpgradeHeaderComponent } from './basic/version-upgrade-header/version-upgrade-header.component';
import { CheckForUpdateService } from './core/service-worker/check-for-update.service-worker.service';
import { ThemeService } from './core/theme/theme.service';
import { WebSocketService } from './core/websocket/websocket.service';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    VersionUpgradeHeaderComponent,
    GlobalLoadingBarComponent,

    NzAlertModule,
    NzSkeletonModule,
    NzModalModule,
    NzButtonModule,

    DarkModeColorDirective,

    ContentModalComponent,
  ],
})
export class AppComponent {
  isOnline = signal<boolean>(true);

  loading = false;

  isMobile = signal<boolean>(false);

  isUpdateModalVisible = computed(() => {
    return this.checkForUpdateService.showUpdateModal();
  });

  constructor(
    private router: Router,
    private themeService: ThemeService,
    private interviewService: InterviewService,
    private webSocketService: WebSocketService,
    private checkForUpdateService: CheckForUpdateService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.themeService.loadTheme(true);

    this.checkForUpdateService.init();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loading = true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loading = false;
      }
    });

    this.createOnline$().subscribe((isOnline) => this.isOnline.set(isOnline));
  }

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((result) => {
      this.isMobile.set(result.matches);
    });
  }

  createOnline$() {
    return merge(
      fromEvent(window, 'offline').pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
      new Observable((sub: Observer<boolean>) => {
        sub.next(navigator.onLine);
        sub.complete();
      })
    );
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.webSocketService.close();
    this.interviewService.stopMicSharing();
    this.interviewService.stopTabSharing();
    return true;
  }
}
